import { useCallback, useEffect, useRef, useState } from 'react';
import { FormHandles } from '@unform/core';
import { Flex, Icon, Image, Stack, Text, useToast } from '@chakra-ui/react';
import { FiPlus, FiSearch } from 'react-icons/fi';

import api from '../../services/api';
import { useAuth } from '../../hooks/auth';
import { AppError } from '../../utils/AppError';
import ImgLoading from '../../assets/loading/tres_pontos.gif';
import InputDate from '../../components/Inputs/InputDate';
import Sidebar from '../../components/Sidebar';
import Loading from '../../components/Loading';
import DTLancamentos from '../../components/ComponentsLancamento/DTLancamentos';
import ModalLancamentoDetail from '../../components/ComponentsLancamento/ModalLancamentoDetail';
import ModalLancamentoEdit from '../../components/ComponentsLancamento/ModalLancamentoEdit';
import { FinJsonDTO } from '../../dtos/FinJsonDTO';
import Alert from '../../components/Alert';

import { Container, Content, Header, Filtros, FormView, ListVazio } from './styles';

type FiltroData = {
	data_i: string;
	data_f: string;
	status: string;
};
type LancamentoData = {
	caixa: string;
	compensado: boolean;
	data: string;
	doc: string;
	fin_json: FinJsonDTO;
	id_lancamento: string;
	name_pay: string;
	nome: string;
	operacao: string;
	operacao_tipo: string;
	url_pay: string;
	vlr_cred: number;
	vlr_cred_formatted?: string;
	vlr_deb: number;
	vlr_deb_formatted?: string;
};

const Lancamento: React.FC = () => {
	const formRef = useRef<FormHandles>(null);
	const { company } = useAuth();
	const toast = useToast();

	const [loading, setLoading] = useState(true);
	const [loadData, setLoadData] = useState(true);

	const [lancamentos, setLancamentos] = useState<LancamentoData[]>([]);
	const [radioStatus, setRadioStatus] = useState('A');

	const [modalLancamentoVisible, setModalLancamentoVisible] = useState(false);
	const [modalLancamentoEditVisible, setModalLancamentoEditVisible] = useState(false);
	const [lancamentoModal, setLancamentoModal] = useState<FinJsonDTO>({} as FinJsonDTO);

	const [openAlert, setOpenAlert] = useState(false);
	const [textAlert, setTextAlert] = useState({ header: '', body: '', is_toast: false });
	const [lancamenotDelete, setLancamentoDelete] = useState<FinJsonDTO>({} as FinJsonDTO);

	async function fetchLancamentos(form: FiltroData) {
		try {
			setLoadData(true);

			const { data } = await api.post('/portal/lancamento', form);
					
			setLancamentos(data);

		} catch (error) {
			const isAppError = error instanceof AppError;
			const msg = isAppError ? error.message : 'Ocorreu um erro ao tentar buscar os lançamentos.';

			toast({
				title: 'Buscar Lançamentos.',
				description: msg,
				status: 'error',
				duration: 3000,
				position: 'top',
				isClosable: true,
				containerStyle: {
					marginTop: '5'
				}
			});

			setLancamentos([]);
		}
		finally {
			setLoadData(false);
		}
	};

	useEffect(() => {
		setLoading(false);
		const fetch = async () => await fetchLancamentos({ status: radioStatus, data_i: '', data_f: '' });
		fetch();
	}, [company]);

	// UseEffect para bloquear a rolagem ao abrir algum modal
	useEffect(() => {
		if (modalLancamentoVisible || modalLancamentoEditVisible) {
			document.body.style.overflow = 'hidden';
		} else {
			document.body.style.overflow = 'auto';
		}
	}, [modalLancamentoVisible, modalLancamentoEditVisible]);

	async function handleForm() {
		const data_i = formRef.current?.getFieldValue('data_i');
		const data_f = formRef.current?.getFieldValue('data_f');

		const dados = {
			status: radioStatus,
			data_i: data_i,
			data_f: data_f
		};

		await fetchLancamentos(dados);
	};

	async function handleDeleteLancamento(lanc: FinJsonDTO) {
		try {
			if (!lanc.documentos) {
				return;
			}

			await api.post('/portal/lancamento/delete', { lancamento: lanc });

		} catch (error) {
			const isAppError = error instanceof AppError;
			const msg = isAppError ? error.message : 'Ocorreu um erro ao tentar deletar o lançamento.';

			toast({
				title: 'Delete Lançamento',
				description: msg,
				status: 'error',
				duration: 3000,
				position: 'top',
				isClosable: true,
				containerStyle: {
					marginTop: '5'
				}
			});

		} finally {
			setOpenAlert(false);
			await fetchLancamentos({ status: radioStatus, data_i: '', data_f: '' });
		}
	};

	return (
		<>
			<Container>
				<Sidebar />

				{loading ? (
					<Loading />
				) : (
					<Content>
						<Header>
							<Flex flexDir="column">
								<h1>Lançamentos</h1>
								<h2>Todo os seus lançamentos aqui.</h2>
							</Flex>

								<Flex flexDir="column" justify="flex-end" pt={[2, 0]}>
									<Stack direction={["column", "row"]} pb={[0, 2]}>
										<button
											onClick={() => {
												setLancamentoModal({} as FinJsonDTO);
												setModalLancamentoEditVisible(true);
											}}
										>
											<Icon as={FiPlus} fontSize={20} />&nbsp;
											NOVO
										</button>
									</Stack>
								</Flex>
						</Header>

						<Filtros>
							<FormView ref={formRef} onSubmit={handleForm}>
								<Flex width="100%" justify="center" mr={[0, 0, 2, 2]}>
									<Flex w="100%" mr={1} flexDirection={["row", "column"]} justify="flex-end">
										<div className="input-content">
											<span>Data Inicial</span>
											<InputDate
												name="data_i"
												placeholder="Data Inicial"
												containerStyle={{ padding: '5px 7px' }}
											/>
										</div>
									</Flex>

									<Flex w="100%" ml={1} flexDirection={["row", "column"]} justify="flex-end">
										<div className="input-content">
											<span>Data Final</span>
											<InputDate
												name="data_f"
												placeholder="Data Final"
												containerStyle={{ padding: '5px 7px' }}
											/>
										</div>
									</Flex>
								</Flex>

								<Flex width="100%" mt={["10px", "10px", "0px"]}>
									<Flex width="100%">
										<div className="radio-group">
											<div className="radio-label" onClick={() => setRadioStatus('A')}>
												<input
													type="radio"
													value="A"
													checked={radioStatus === 'A'}
													onChange={() => setRadioStatus('A')}
												/>
												<label>Abertas</label>
											</div>

											<div className="radio-label" onClick={() => setRadioStatus('B')}>
												<input
													type="radio"
													value="B"
													checked={radioStatus === 'B'}
													onChange={() => setRadioStatus('B')}
												/>
												<label>Baixadas</label>
											</div>

											<div className="radio-label" onClick={() => setRadioStatus('T')}>
												<input
													type="radio"
													value="T"
													checked={radioStatus === 'T'}
													onChange={() => setRadioStatus('T')}
												/>
												<label>Todas</label>
											</div>
										</div>
									</Flex>

									<Flex flexDirection="column" justify="flex-end">
										<button type="submit">
											<FiSearch /> <span>Pesquisar</span>
										</button>
									</Flex>
								</Flex>
							</FormView>
						</Filtros>

						{loadData ? (
							<Flex w="100%" justify="center" align="center">
								<Image src={ImgLoading} w="100px" h="100px" />
							</Flex>
						) : (lancamentos.length > 0 ? (
							<DTLancamentos
								data={lancamentos}
								setLancamentoModal={setLancamentoModal}
								setModalLancamentoVisible={setModalLancamentoVisible}
								setModalLancamentoEditVisible={setModalLancamentoEditVisible}
								setLancamentoDelete={setLancamentoDelete}
								setOpenAlert={setOpenAlert}
								setTextAlert={setTextAlert}
							/>
						) : (
							<ListVazio>
								<Text fontSize={18}>Nenhum financeiro neste período.</Text>
							</ListVazio>
						)
						)}
					</Content>
				)}
			</Container>

			{modalLancamentoVisible && (
				<ModalLancamentoDetail
					onClose={() => setModalLancamentoVisible(false)}
					isVisible={modalLancamentoVisible}
					lancamento={lancamentoModal}
				/>
			)}

			{modalLancamentoEditVisible && (
				<ModalLancamentoEdit
					onClose={() => setModalLancamentoEditVisible(false)}
					isVisible={modalLancamentoEditVisible}
					lancamento={lancamentoModal}
					handleForm={handleForm}
				/>
			)}

			{openAlert && (
				<Alert
					isOpen={openAlert}
					onRequestClose={setOpenAlert}
					textAlert={textAlert}
					execFunction={async () => await handleDeleteLancamento(lancamenotDelete)}
				/>
			)}
		</>
	)
};

export default Lancamento;